import { skipToken } from "@reduxjs/toolkit/query";
import type { CustomFieldDefinition } from "@repo/app-types/entities/customField";
import { selectPopulationFilters } from "@repo/app-types/entities/filters";
import type { OutcomeDefinition } from "@repo/app-types/entities/outcome";
import {
  getOutcomeDefinitionFromPlaybook,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import {
  asTeammateId,
  type TeammateId,
} from "@repo/app-types/entities/teammate";
import type {
  TeammateAccountGrading,
  TranscriptMatchesByGrading,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeaderFilterBarPortal } from "@repo/ui/Navigation/pageHeaderFilterBarSlot";
import type { EntityTableColumnDefinition } from "@repo/ui/tables/entityTable";
import { useNow } from "@repo/ui/timestamps/timestampContext";
import {
  SegmentedControl,
  type SegmentedControlOption,
} from "@repo/ui/toggleButton/segmentedControl";
import { useMemo, useState, type FC, type ReactNode } from "react";
import { useParams, useSearchParams } from "react-router";
import {
  usePlaybookTeammateMostRecentGradingsCountQuery,
  usePlaybookTeammateMostRecentGradingsInfiniteQuery,
} from "../../api/entities/performancePlaybook.js";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { PageBody, TableCard } from "../../components/layout/pageLayout.js";
import {
  NavigationSlideoverContainer,
  useModalNavigation,
} from "../../hooks/useModalNavigation.js";
import {
  buildTeamPerformancePageQueryArgs,
  checkHasNarrowingFilters,
} from "../filters/buildFilterQueryArgs.js";
import { useFilters } from "../filters/filters.js";
import { useAgentPlaybookFilterDefaults } from "../filters/usePlaybookFilterDefaults.js";
import { buildCallActivityColumns } from "../teammatePerformance/callActivityColumns.js";
import {
  buildGradingModalPath,
  buildTranscriptSearchNavState,
} from "../teammatePerformance/gradingHelpers.js";
import { GradingsTable } from "../teammatePerformance/gradingsTable.js";
import { DisplayColumnsSelector } from "../teammatePerformance/hooks/displayColumnsSelector.js";
import { useCustomFieldColumnSelector } from "../teammatePerformance/hooks/useCustomFieldColumnSelector.js";
import {
  useTranscriptSearch,
  type TranscriptSearch,
} from "../teammatePerformance/hooks/useTranscriptSearch.js";
import {
  TeammateAgentUpcomingCalls,
  useUpcomingCallsFilters,
} from "./teammateAgentUpcomingCalls.js";

type CallsView = "recent" | "upcoming";

const callsViewOptions: readonly SegmentedControlOption<CallsView>[] = [
  { id: "recent", label: "Recent" },
  { id: "upcoming", label: "Upcoming" },
];

export const TeammateAgentCallsTab: FC = () => {
  const { teammateAgentId: teammateAgentIdParam } = useParams();
  if (!teammateAgentIdParam) {
    throw new Error("Missing teammateAgentId param.");
  }
  const teammateAgentId = asTeammateId(teammateAgentIdParam);

  // Keyed so the view and the searches start fresh for each agent: React
  // Router reuses this element when navigating between agents' Calls pages.
  return (
    <TeammateAgentCalls
      key={teammateAgentId}
      teammateAgentId={teammateAgentId}
    />
  );
};

interface TeammateAgentCallsProps {
  teammateAgentId: TeammateId;
}

/**
 * Owns the view and both views' searches, so they survive toggling between
 * views and opening and closing a slideover, while only the shown view's list
 * mounts and queries.
 */
const TeammateAgentCalls: FC<TeammateAgentCallsProps> = (props) => {
  const { teammateAgentId } = props;

  const {
    selectedCustomFieldDefinitions,
    selectSections: customFieldSections,
    selectedIds: customFieldSelectedIds,
    onSelectionChange: onCustomFieldSelectionChange,
  } = useCustomFieldColumnSelector({
    includeDataExtractionFields: true,
    preferenceScope: { type: "teammateAgent", teammateAgentId },
  });

  const { data: teammateAgent, isLoading: isLoadingTeammateAgent } =
    useGetTeammateAgentQuery(teammateAgentId);

  const playbookId = teammateAgent?.teammate_agent_config.playbook_id;

  // Gated on the agent having ever been given a call task, not on outbound
  // being enabled: the columns should keep showing the history of a queue that
  // has since drained, and stay out of the way of agents that only take calls.
  const showsCallActivity = teammateAgent?.has_call_tasks === true;

  const callActivityColumns = useMemo(
    () => (showsCallActivity ? buildCallActivityColumns() : undefined),
    [showsCallActivity],
  );

  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);
  const outcomeDefinition = getOutcomeDefinitionFromPlaybook(playbook);

  // `?view=upcoming` picks the initial view, so a reload or a shared link
  // lands on it. After that the state is the authority: a slideover's own
  // links drop the param, and closing one shouldn't switch the list behind it.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedView, setSelectedView] = useState<CallsView>(() =>
    searchParams.get("view") === "upcoming" ? "upcoming" : "recent",
  );
  // Upcoming exists only for an agent that has been given call tasks.
  const view: CallsView = showsCallActivity ? selectedView : "recent";

  const transcriptSearch = useTranscriptSearch();
  const upcomingFilters = useUpcomingCallsFilters();

  if (isLoadingTeammateAgent || !teammateAgent || !playbookId) {
    return null;
  }

  const rightActions = (
    <>
      {showsCallActivity ? (
        <SegmentedControl
          aria-label="Calls to show"
          size={Size.SMALL}
          options={callsViewOptions}
          value={view}
          onChange={(newView) => {
            setSelectedView(newView);
            // Replaced rather than pushed: Back leaves the page instead of
            // walking back through toggles the state no longer follows.
            setSearchParams(
              (current) => {
                const next = new URLSearchParams(current);
                if (newView === "upcoming") {
                  next.set("view", "upcoming");
                } else {
                  next.delete("view");
                }
                return next;
              },
              { replace: true },
            );
          }}
        />
      ) : null}
      <DisplayColumnsSelector
        customFieldSections={customFieldSections}
        customFieldSelectedIds={customFieldSelectedIds}
        onCustomFieldSelectionChange={onCustomFieldSelectionChange}
      />
    </>
  );

  return (
    <>
      {view === "upcoming" ? (
        <TeammateAgentUpcomingCalls
          playbookId={playbookId}
          teammateAgentId={teammateAgentId}
          outcomeDefinition={outcomeDefinition}
          selectedCustomFieldDefinitions={selectedCustomFieldDefinitions}
          callActivityColumns={callActivityColumns}
          filters={upcomingFilters}
          rightActions={rightActions}
        />
      ) : (
        <TeammateAgentRecentCalls
          playbookId={playbookId}
          teammateAgentId={teammateAgentId}
          outcomeDefinition={outcomeDefinition}
          selectedCustomFieldDefinitions={selectedCustomFieldDefinitions}
          callActivityColumns={callActivityColumns}
          transcriptSearch={transcriptSearch}
          rightActions={rightActions}
        />
      )}

      <NavigationSlideoverContainer />
    </>
  );
};

interface TeammateAgentRecentCallsProps {
  playbookId: PlaybookId;
  teammateAgentId: TeammateId;
  outcomeDefinition: OutcomeDefinition | undefined;
  selectedCustomFieldDefinitions: readonly CustomFieldDefinition[];
  callActivityColumns:
    | readonly EntityTableColumnDefinition<TeammateAccountGrading>[]
    | undefined;
  transcriptSearch: TranscriptSearch;
  rightActions: ReactNode;
}

/** The agent's interactions that have happened, under the shared filters. */
const TeammateAgentRecentCalls: FC<TeammateAgentRecentCallsProps> = (props) => {
  const { playbookId, teammateAgentId } = props;

  const modalNavigation = useModalNavigation();
  const now = useNow();
  const filterDefaults = useAgentPlaybookFilterDefaults(playbookId, "calls");
  const [
    {
      dateRange,
      dateBasis,
      outcome,
      customFields,
      playbookConditions,
      signalId,
      signalMetadataFields,
      totalRecordingDuration,
      recordingCount,
    },
    renderFilters,
    hasNarrowingChanges,
  ] = useFilters(
    [
      "dateRange",
      "dateBasis",
      "outcome",
      "customFields",
      "playbookConditions",
      "signalId",
      "signalMetadataFields",
      "totalRecordingDuration",
      "recordingCount",
    ],
    { playbookId, view: "calls" },
    filterDefaults,
  );

  const { search, renderSearchInput, deepSearchEnabled } =
    props.transcriptSearch;
  const deepSearchEnabledArg = search ? deepSearchEnabled : undefined;

  const baseArgs = buildTeamPerformancePageQueryArgs(now, {
    playbookId,
    dateRange,
    dateBasis,
    teammateGroupIds: null,
    outcome,
    customFields,
    playbookConditions,
    signalId,
    signalMetadataFields,
    totalRecordingDuration,
    recordingCount,
  });

  // The "N of M" denominator: the same population the list is drawn from (so
  // it follows "Include calls without grades") minus the user's own filters.
  // Suppressed unless the user narrowed something that reaches the filters,
  // where the two queries would be identical.
  const showsDenominator =
    hasNarrowingChanges && checkHasNarrowingFilters(baseArgs.filters);

  const populationArgs = showsDenominator
    ? {
        playbookId,
        teammateId: teammateAgentId,
        scope: baseArgs.scope,
        filters: selectPopulationFilters(baseArgs.filters),
        search,
        deepSearchEnabled: deepSearchEnabledArg,
      }
    : skipToken;

  const filteredArgs = {
    playbookId,
    teammateId: teammateAgentId,
    scope: baseArgs.scope,
    filters: baseArgs.filters,
    search,
    deepSearchEnabled: deepSearchEnabledArg,
  };

  // `currentData`, not `data`: the latter falls back to the previous args'
  // result whenever the current request isn't successful, which for the
  // denominator means a failed refetch can leave M behind N ("80 of 45").
  const {
    currentData: totalGradingCount,
    isFetching: isFetchingTotalGradingCount,
  } = usePlaybookTeammateMostRecentGradingsCountQuery(populationArgs);

  const { data: gradingCount, isFetching: isFetchingGradingCount } =
    usePlaybookTeammateMostRecentGradingsCountQuery(filteredArgs);

  const {
    data: gradingsData,
    isFetching: isFetchingGradings,
    isError: isErrorGradings,
    hasNextPage: canFetchMoreGradings,
    fetchNextPage: fetchMoreGradings,
    isFetchingNextPage: isFetchingMoreGradings,
  } = usePlaybookTeammateMostRecentGradingsInfiniteQuery(filteredArgs);

  const gradings = useMemo(
    () => gradingsData?.pages.flatMap((page) => page.recentGradings) ?? [],
    [gradingsData],
  );

  const snippetsByGradingId = useMemo(
    () =>
      Object.assign(
        {},
        ...(gradingsData?.pages.map((page) => page.snippets) ?? []),
      ) as TranscriptMatchesByGrading,
    [gradingsData],
  );

  // True while the gradings count/list is loading the full result set — the
  // initial fetch or a refetch from a filter/search change — but not while
  // appending more pages (`isFetchingMoreGradings`).
  const isLoadingGradings =
    isFetchingTotalGradingCount ||
    isFetchingGradingCount ||
    (isFetchingGradings && !isFetchingMoreGradings);

  return (
    <>
      <PageHeaderFilterBarPortal>
        {renderFilters({
          isLoading: isLoadingGradings,
          // Explicitly gated rather than relying on the skipped query: RTK
          // Query's `data` keeps serving the last successful result once args
          // become `skipToken`, which would strand a stale denominator.
          denominatorCount: showsDenominator ? totalGradingCount : undefined,
          count: gradingCount,
          leadingItems: renderSearchInput({
            resultCount: gradingCount,
            isLoadingResults: isLoadingGradings,
          }),
          rightActions: props.rightActions,
        })}
      </PageHeaderFilterBarPortal>
      <PageBody>
        <TableCard>
          <GradingsTable
            outcomeDefinition={props.outcomeDefinition}
            gradings={gradings}
            snippetsByGradingId={snippetsByGradingId}
            isLoading={isLoadingGradings}
            isError={isErrorGradings}
            onLoadMore={canFetchMoreGradings ? fetchMoreGradings : undefined}
            onGradingAction={(grading) => {
              const gradingSnippets = snippetsByGradingId[grading.id];
              modalNavigation.push(
                buildGradingModalPath(grading.id),
                search?.trim() && gradingSnippets?.length
                  ? buildTranscriptSearchNavState(search, gradingSnippets)
                  : undefined,
              );
            }}
            selectedCustomFieldDefinitions={
              props.selectedCustomFieldDefinitions
            }
            additionalColumns={props.callActivityColumns}
            showsPlaybookColumn={false}
            isFullBleed
          />
        </TableCard>
      </PageBody>
    </>
  );
};
